import { useState } from "react";
import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { VariantProps } from "~/components/article/article-speech/article-speech-inline-widget/variants/types";
import { ZIndexLayer } from "~/lib/styles";
import type { genericArticleSpeechInlineWidgetGenericArticle$key } from "~/queries/__generated__/genericArticleSpeechInlineWidgetGenericArticle.graphql";

import { ArticleSpeechInlineWidgetGenericButton } from "./button";
import type { HeadphoneIconTheme } from "./button/headphone-icon";
import { EmptyButton } from "./empty-button";
import {
  useEnableGenericInlineWidgetByUserSubscription,
  useShowGenericInlineWidgetByArticle,
} from "./hooks";
import { Container, Wrapper } from "./styles";
import { ArticleSpeechWidgetToolTip } from "./tool-tip";

type Props = {
  extendClick?: () => void;
  headphoneIconTheme?: HeadphoneIconTheme;
  isMinify?: boolean;
  reference: genericArticleSpeechInlineWidgetGenericArticle$key;
  withMinifyText?: boolean;
  zIndex?: string;
} & VariantProps;

export const ArticleSpeechInlineWidgetGeneric: FunctionComponent<Props> = ({
  className,
  extendClick,
  headphoneIconTheme = "grey",
  isMinify = false,
  reference,
  withMinifyText = true,
  zIndex = `${ZIndexLayer.Drawer - 1}`,
}) => {
  const article = useFragment(
    graphql`
      fragment genericArticleSpeechInlineWidgetGenericArticle on Article {
        ...buttonArticleSpeechInlineWidgetGenericButtonArticle
        ...hooksShowGenericInlineWidgetByArticleArticle
        ...toolTipArticleSpeechWidgetToolTipArticle
      }
    `,
    reference,
  );
  const { isWidgetVisibleByType } = useShowGenericInlineWidgetByArticle(article);
  const { isWidgetEnabledByUser } = useEnableGenericInlineWidgetByUserSubscription();
  const [isTooltipActive, setIsTooltipActive] = useState(false);

  return (
    <Wrapper $isMinify={isMinify}>
      {isWidgetVisibleByType ? (
        isWidgetEnabledByUser === undefined ? (
          <EmptyButton className={className} isMinify={isMinify} />
        ) : (
          <Container className={className}>
            <ArticleSpeechWidgetToolTip
              isTooltipActive={isTooltipActive}
              reference={article}
              setIsTooltipActive={setIsTooltipActive}
            >
              <ArticleSpeechInlineWidgetGenericButton
                extendClick={extendClick}
                headphoneIconTheme={headphoneIconTheme}
                isMinify={isMinify}
                reference={article}
                setIsTooltipActive={setIsTooltipActive}
                withMinifyText={withMinifyText}
                zIndex={zIndex}
              />
            </ArticleSpeechWidgetToolTip>
          </Container>
        )
      ) : null}
    </Wrapper>
  );
};

ArticleSpeechInlineWidgetGeneric.displayName = "ArticleSpeechInlineWidgetGeneric";
